import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "sip-pwa-install-dismissed-at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isRecentlyDismissed() {
  try {
    const at = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return at && Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * PWA install banner.
 *  - On Android/Chrome/Edge: triggers `beforeinstallprompt` → button calls `prompt()`.
 *  - On iOS Safari: there's no install API; show "Tambah ke Home Screen" instructions.
 *  - Auto-hides if app already installed (display-mode: standalone) or recently dismissed.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;

    const isIOS =
      /iphone|ipad|ipod/i.test(navigator.userAgent) &&
      !/crios|fxios|edgios/i.test(navigator.userAgent);

    if (isIOS) {
      // Delay slightly so it doesn't pop on first paint.
      const t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 2500);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      // ignore
    }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 z-[9999] -translate-x-1/2 w-[min(92vw,420px)] rounded-2xl border border-slate-700 bg-slate-900 text-white shadow-2xl"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-red-600/90">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Install SIPILKADES</p>
          {iosHint ? (
            <p className="mt-1 text-xs leading-relaxed text-slate-300">
              Buka ikon{" "}
              <Share className="inline h-3.5 w-3.5 -mt-0.5" /> di Safari, lalu pilih{" "}
              <b>&quot;Tambah ke Layar Utama&quot;</b> untuk akses 1-tap.
            </p>
          ) : (
            <p className="mt-1 text-xs leading-relaxed text-slate-300">
              Pasang aplikasi ke layar utama untuk akses lebih cepat & gunakan saat offline (DPS/DPSHP yang sudah dibuka).
            </p>
          )}
          {!iosHint && deferred && (
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold hover:bg-red-700 active:scale-95 transition"
            >
              <Download className="h-3.5 w-3.5" />
              Install Sekarang
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Tutup"
          data-testid="pwa-install-dismiss"
          className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
